import { useState, useRef, useEffect } from "react";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import styles from "./WatchlistCard.module.css";
import { WATCHLIST_BOOKMARK_PALETTE } from "./watchlistPriceData";

function WatchlistCard({ item, onColorChange, colorUpdatingId, onDelete, deletingId }) {
  const {
    id,
    name,
    date,
    lastPrice,
    change,
    changeIsPositive,
    barColor,
    colorIndex,
  } = item;

  const [showPalette, setShowPalette] = useState(false);
  const paletteAnchorRef = useRef(null);
  const isUpdatingColor = colorUpdatingId === id;
  const isDeleting = deletingId === id;

  useEffect(() => {
    if (!showPalette) return;
    const handleClickOutside = (e) => {
      if (paletteAnchorRef.current && !paletteAnchorRef.current.contains(e.target)) {
        setShowPalette(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPalette]);

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (isUpdatingColor) return;
    setShowPalette((prev) => !prev);
  };

  const handleColorSelect = async (e, nextColorIndex) => {
    e.stopPropagation();
    if (isUpdatingColor || nextColorIndex === colorIndex) {
      setShowPalette(false);
      return;
    }

    try {
      await onColorChange?.(item, nextColorIndex);
    } finally {
      setShowPalette(false);
    }
  };

  return (
    <div className={styles["watchlist-card"]}>
      <div ref={paletteAnchorRef} className={styles["watchlist-card-icon-wrap"]} aria-hidden>
        <div className={styles["watchlist-card-icon-inner"]}>
          <button
            type="button"
            className={styles["watchlist-card-icon-btn"]}
            onClick={handleIconClick}
            aria-label="Open color palette"
            aria-expanded={showPalette}
            disabled={isUpdatingColor}
          >
            <GlobalIcon
              iconName="WatchlistCardIcon"
              className={styles["watchlist-card-icon-svg"]}
              style={{ color: barColor, fill: barColor }}
            />
          </button>
        </div>
        {showPalette && (
          <div className={styles["watchlist-card-palette"]}>
            {WATCHLIST_BOOKMARK_PALETTE.map(({ id: paletteId, value }) => {
              const isSelected = Number(paletteId) === Number(colorIndex);
              return (
                <button
                  key={paletteId}
                  type="button"
                  className={`${styles["watchlist-card-swatch"]} ${
                    isSelected ? styles["watchlist-card-swatch-selected"] : ""
                  }`}
                  style={{ backgroundColor: value }}
                  onClick={(e) => handleColorSelect(e, Number(paletteId))}
                  disabled={isUpdatingColor}
                  aria-label={`Select color ${paletteId}`}
                  aria-pressed={isSelected}
                />
              );
            })}
          </div>
        )}
      </div>
      <div className={styles["watchlist-card-left"]}>
        <div className={styles["watchlist-card-info"]}>
          <div className={styles["watchlist-card-name"]}>{name}</div>
          <div className={styles["watchlist-card-date"]}>{date}</div>
        </div>
        <div className={styles["watchlist-card-actions"]}>
          <span className={styles["action-badge"]} style={{ backgroundColor: "#E8FCF1", color: "#0D9668" }}>B</span>
          <span className={styles["action-badge"]} style={{ backgroundColor: "#FFEBEB", color: "#DC2626" }}>S</span>
        </div>
      </div>
      <div className={styles["watchlist-card-right"]}>
        <div className={styles["watchlist-card-price"]}>{lastPrice}</div>
        <div
          className={`${styles["watchlist-card-change"]} ${
            changeIsPositive ? styles["positive"] : styles["negative"]
          }`}
        >
          {changeIsPositive ? "+" : ""}
          {change}%
        </div>
      </div>
      <button
        type="button"
        className={styles["watchlist-card-delete"]}
        title="Remove"
        aria-label="Remove from watchlist"
        onClick={() => onDelete?.(item)}
        disabled={isDeleting || isUpdatingColor}
      >
        <GlobalIcon iconName="BinIcon" className={styles["watchlist-card-delete-icon"]} />
      </button>
    </div>
  );
}

export default WatchlistCard;
